<template>
    <article class="setup-card">
        <!-- Use the perspective mark because cropped paintings looked like unloaded images at this size.
             -->
        <PerspectiveMark class="mark" :animate="false" />

        <div class="text">
            <h3 class="card-title">{{ title }}</h3>
            <p class="lede">{{ lede }}</p>
        </div>

        <BaseLink :href="card.url" class="base-btn-primary cta" @click="handleClick">{{ ctaLabel }}</BaseLink>
    </article>
</template>

<script setup lang="ts">
/**
 * Replaces only the Perspectives card to avoid duplicate assessment prompts. Other cards retain priority.
 * The server omits this card once all setup asks are satisfied.
 */
import PerspectiveMark from "~vue/icons/PerspectiveMark.vue";
import { setupCtaLabel } from "~vue/ProfileSetup/setupAsks";
import type { SetupHomeCard } from "~vue/ProfileSetup/types";
import BaseLink from "~vue/router/BaseLink.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, onMounted } from "vue";

const { card } = defineProps<{ card: SetupHomeCard }>();

const ctaLabel = computed(() => setupCtaLabel(card.steps, card.completed_count));

const title = "Your work, not work in general";

const lede = computed(() =>
    card.completed_count === 0
        ? "Set up four quick things, and coaching advice goes from generic to specific to your situation."
        : "Finish setting up, and coaching advice goes from generic to specific to your situation.",
);

const eventProps = computed(() => ({
    completed_count: card.completed_count,
    total: card.total,
    next_step: card.next_step,
}));

onMounted(() => logUserInteraction(AmplitudeEventNames.GUIDED_SETUP_HOMEPAGE_CARD_SHOWN, eventProps.value));

function handleClick(): void {
    logUserInteraction(AmplitudeEventNames.GUIDED_SETUP_HOMEPAGE_CARD_CLICKED, eventProps.value);
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    /* The footprint of the card this one replaces, so the row keeps its shape
       whichever of the two is offered. */
    .setup-card {
        @apply flex min-h-[305px] flex-col items-start justify-end gap-4 rounded-lgr border border-border bg-surface-raised p-5;
    }

    .mark {
        @apply h-11 w-auto shrink-0;
    }

    .text {
        @apply mb-2 flex flex-col gap-1;
    }

    /* Not `.title`: that is a 30px display utility, and Tailwind's utilities layer
       outranks this one, so the class name alone would resize the heading. */
    .card-title {
        @apply title-body text-pretty text-ink;
    }

    .lede {
        @apply body-small text-ink-muted;
    }

    .cta {
        @apply mt-auto inline-flex w-full items-center justify-center;
    }
}
</style>
